import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import { Card, Button, Input, Textarea, Select, Loader } from "@/components/ui/Primitives";
import { CheckCircle2, ArrowLeft, Sparkles, TrendingUp, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function ApplyPage() {
  const { propertyId } = useParams();
  const nav = useNavigate();
  const [propData, setPropData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    current_address: "", current_city: "", current_province: "ON", current_postal: "",
    employment_status: "employed", monthly_income_cents: "",
    occupants: 1, move_in_date: "",
    pets: "", smoker: false, vehicles: "",
    notes: "",
    consent_background_check: true, consent_credit_check: true,
    e_signature: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/property/${propertyId}`);
        setPropData(data);
      } finally { setLoading(false); }
    })();
  }, [propertyId]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/applications", {
        property_id: propertyId,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        current_address: form.current_address || null,
        current_city: form.current_city || null,
        current_province: form.current_province || null,
        current_postal: form.current_postal || null,
        employment_status: form.employment_status,
        monthly_income_cents: form.monthly_income_cents ? Math.round(Number(form.monthly_income_cents) * 100) : null,
        occupants: Number(form.occupants) || 1,
        move_in_date: form.move_in_date || null,
        pets: form.pets || null,
        smoker: !!form.smoker,
        vehicles: form.vehicles || null,
        notes: form.notes || null,
        consent_background_check: !!form.consent_background_check,
        consent_credit_check: !!form.consent_credit_check,
        e_signature: form.e_signature || null,
        rental_history: [],
        references: [],
      });
      setSubmitted(true);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader/></div>;
  const property = propData?.property || propData || {};

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicHeader light/>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-9 h-9"/>
          </div>
          <h1 className="font-display text-4xl font-bold mt-6 text-slate-950">Application submitted</h1>
          <p className="text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
            Thanks {form.full_name.split(" ")[0]} — the property manager for <b>{property.name}</b> will review your application and reach out within 2–3 business days.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={`/p/${propertyId}`}><Button variant="secondary">View property</Button></Link>
            <Link to="/signup"><Button>Create tenant account</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader light/>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to={`/p/${propertyId}`} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-5" data-testid="back-to-property">
          <ArrowLeft className="w-4 h-4"/> Back to property
        </Link>

        <div className="mb-7">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-green-800">Rental application</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-slate-950 mt-3">
            Apply for {property.name || "this property"}
          </h1>
          <p className="text-slate-600 mt-2">All fields marked with * are required. Takes ~3 minutes.</p>
        </div>

        <Card className="p-6 md:p-8">
          <form onSubmit={submit} className="space-y-6" data-testid="application-form">
            <Section title="About you">
              <Input label="Full name *" required value={form.full_name} onChange={(e)=>setForm({...form, full_name:e.target.value})} testid="apply-name"/>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input label="Email *" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} testid="apply-email"/>
                <Input label="Phone" type="tel" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} testid="apply-phone"/>
              </div>
            </Section>

            <Section title="Current address">
              <Input label="Street address" value={form.current_address} onChange={(e)=>setForm({...form, current_address:e.target.value})}/>
              <div className="grid sm:grid-cols-3 gap-4">
                <Input label="City" value={form.current_city} onChange={(e)=>setForm({...form, current_city:e.target.value})}/>
                <Select label="Province" value={form.current_province} onChange={(e)=>setForm({...form, current_province:e.target.value})}>
                  {["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"].map(p => <option key={p}>{p}</option>)}
                </Select>
                <Input label="Postal code" value={form.current_postal} onChange={(e)=>setForm({...form, current_postal:e.target.value})}/>
              </div>
            </Section>

            <Section title="Employment & income">
              <div className="grid sm:grid-cols-2 gap-4">
                <Select label="Status" value={form.employment_status} onChange={(e)=>setForm({...form, employment_status:e.target.value})}>
                  <option value="employed">Employed</option>
                  <option value="self_employed">Self-employed</option>
                  <option value="student">Student</option>
                  <option value="retired">Retired</option>
                  <option value="other">Other</option>
                </Select>
                <Input label="Monthly income (CAD)" type="number" step="0.01" value={form.monthly_income_cents} onChange={(e)=>setForm({...form, monthly_income_cents:e.target.value})}/>
              </div>
            </Section>

            <Section title="Move-in details">
              <div className="grid sm:grid-cols-3 gap-4">
                <Input label="Move-in date" type="date" value={form.move_in_date} onChange={(e)=>setForm({...form, move_in_date:e.target.value})}/>
                <Input label="Occupants" type="number" min="1" value={form.occupants} onChange={(e)=>setForm({...form, occupants:e.target.value})}/>
                <Input label="Vehicles" placeholder="e.g. 1 car" value={form.vehicles} onChange={(e)=>setForm({...form, vehicles:e.target.value})}/>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input label="Pets" placeholder="None / 1 small dog" value={form.pets} onChange={(e)=>setForm({...form, pets:e.target.value})}/>
                <label className="flex items-center gap-2.5 text-sm text-slate-700 mt-7">
                  <input type="checkbox" checked={form.smoker} onChange={(e)=>setForm({...form, smoker:e.target.checked})} className="w-4 h-4 rounded text-green-800 focus:ring-green-800"/>
                  Smoker
                </label>
              </div>
            </Section>

            <Section title="Anything else?">
              <Textarea label="Notes (optional)" placeholder="Tell us anything that strengthens your application…" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})}/>
            </Section>

            <MatchScorePreviewPanel propertyId={propertyId} form={form}/>

            <Section title="Consent & signature">
              <div className="space-y-2.5">
                <label className="flex items-start gap-2.5 text-sm text-slate-700">
                  <input type="checkbox" checked={form.consent_background_check} onChange={(e)=>setForm({...form, consent_background_check:e.target.checked})} className="w-4 h-4 rounded text-green-800 mt-0.5"/>
                  <span>I consent to a background check.</span>
                </label>
                <label className="flex items-start gap-2.5 text-sm text-slate-700">
                  <input type="checkbox" checked={form.consent_credit_check} onChange={(e)=>setForm({...form, consent_credit_check:e.target.checked})} className="w-4 h-4 rounded text-green-800 mt-0.5"/>
                  <span>I consent to a credit check.</span>
                </label>
              </div>
              <Input label="Type your full name as e-signature" placeholder="Your full legal name" value={form.e_signature} onChange={(e)=>setForm({...form, e_signature:e.target.value})} testid="apply-esignature"/>
            </Section>

            <div className="pt-2 flex items-center justify-end gap-3">
              <Link to={`/p/${propertyId}`}><Button type="button" variant="ghost">Cancel</Button></Link>
              <Button type="submit" disabled={submitting} data-testid="apply-submit-button">{submitting ? "Submitting…" : "Submit application"}</Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-4 pb-6 border-b border-slate-100 last:border-0 last:pb-0">
      <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
      {children}
    </section>
  );
}

/**
 * Shows the tenant a real-time Smart Mark "Match Score" against the
 * listing they're applying for. Triggered manually so we don't burn
 * tokens on every keystroke.
 */
function MatchScorePreviewPanel({ propertyId, form }) {
  const [score, setScore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const canScore = !!(form.full_name && form.move_in_date);

  async function compute() {
    setBusy(true); setError(""); setScore(null);
    try {
      const r = await fetch(`${API}/api/match-score/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listing_id: propertyId,
          full_name: form.full_name || null,
          occupation: form.employment_status || null,
          employment_status: form.employment_status || null,
          monthly_income_cents: form.monthly_income_cents ? Math.round(Number(form.monthly_income_cents) * 100) : null,
          move_in_date: form.move_in_date || null,
          occupants: Number(form.occupants) || 1,
          pets: form.pets || null,
          smoker: !!form.smoker,
          province: form.current_province || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not compute score");
      setScore(d);
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  }

  const tone = score ? (score.score >= 75 ? "green" : score.score >= 50 ? "amber" : "rose") : "slate";
  const TONE = {
    green: { bg: "bg-green-50",  border: "border-green-300",  text: "text-green-900",  badge: "bg-green-700 text-white",  icon: "text-green-700" },
    amber: { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-900",  badge: "bg-amber-700 text-white",  icon: "text-amber-700" },
    rose:  { bg: "bg-rose-50",   border: "border-rose-300",   text: "text-rose-900",   badge: "bg-rose-700 text-white",   icon: "text-rose-700" },
    slate: { bg: "bg-slate-50",  border: "border-slate-200",  text: "text-slate-700",  badge: "bg-slate-700 text-white",  icon: "text-slate-500" },
  }[tone];

  return (
    <section className="space-y-4 pb-6 border-b border-slate-100" data-testid="match-score-panel">
      <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-emerald-700"/> See your match score
      </h3>
      <p className="text-sm text-slate-600">Smart Mark compares what you've filled in against the listing's requirements and gives you a 0-100 compatibility score — landlords prioritise stronger fits.</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" disabled={!canScore || busy} onClick={compute} data-testid="match-score-compute">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin inline mr-1.5"/> Computing…</> : score ? "Recalculate" : "Calculate my match score"}
        </Button>
        {!canScore && <span className="text-xs text-slate-500">Fill in your name + move-in date first.</span>}
      </div>
      {error && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">{error}</div>}
      {score && (
        <div className={`rounded-xl border ${TONE.border} ${TONE.bg} p-5`} data-testid="match-score-result">
          <div className="flex items-start gap-5">
            <div className={`flex-shrink-0 w-20 h-20 rounded-full ${TONE.badge} flex items-center justify-center`}>
              <div className="text-center leading-tight">
                <div className="font-display text-3xl font-extrabold" data-testid="match-score-value">{score.score}</div>
                <div className="text-[10px] uppercase tracking-widest opacity-80">/ 100</div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-display text-base font-bold ${TONE.text}`} data-testid="match-score-headline">{score.headline}</div>
              <p className={`text-sm mt-1 ${TONE.text} opacity-90`}>{score.reasoning}</p>
            </div>
          </div>
          {(score.strengths?.length || 0) > 0 && (
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-green-800 mb-1.5 flex items-center gap-1"><TrendingUp className="w-3 h-3"/> Strengths</div>
                <ul className="space-y-1">
                  {score.strengths.map((s, i) => <li key={i} className="text-xs text-slate-700">+ {s}</li>)}
                </ul>
              </div>
              {(score.concerns?.length || 0) > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 mb-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Concerns</div>
                  <ul className="space-y-1">
                    {score.concerns.map((s, i) => <li key={i} className="text-xs text-slate-700">– {s}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
